import { FC, useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import GridContainer from 'components/GridContainer';
import { Step, StepLabel, Stepper, Grid } from '@mui/material';
import Spacing from 'components/Spacing';
import FormWrapper, { FormWrapperRef } from './FormWrapper';
import CompanyDetails from './CompanyForm/CompanyDetails';
import { useGetRole } from 'hooks/ui/useGetRole';
import DealDetailsForm from './DealDetailsForm';
import ReviewSection from './ReviewSection';
import SuccessPage from './SuccessPage';
import { InitialTargetShareOption } from 'further-types/allocations';
import { Api } from 'further-types/allocations';
import { useParams } from 'react-router-dom';
import api from 'lib/trpcClient';
import PageLoader from 'components/PageLoader';
import { Steps } from './shared/steps';

const stepLabels = {
  [Steps.CompanyDetails]: 'Enter company details',
  [Steps.DealDetails]: 'Enter deal details and tranches',
  [Steps.Review]: 'Review and confirm',
};

// Move the schema and type here so they can be shared
const dealDetailsSchema = z.object({
  dealSharePrice: z
    .number()
    .gt(0, { message: 'Please enter a valid share price' }),
  allocationDate: z.date({ message: 'Please enter a valid date' }),
  trancheIds: z
    .array(z.string())
    .min(1, { message: 'Please select at least one tranche' }),
});

export type DealDetailsFormValues = {
  dealSharePrice: number;
  allocationDate: Date | null;
  trancheIds: Array<string>;
};

// Add FundAllocation type for sharing across components
export type FundAllocation = {
  companyId: string;
  trancheId: string;
  trancheName: string;
  totalSharesAllocated: number;
  targetShares: number;
  investmentsAllocatedTo: number;
  investmentsInTranche: number;
  finalDeal: boolean;
  totalCapitalAllocated: number;
  dealSharePrice: number;
  defaultTransactionFeeVatPercent?: number;
  allocationDate: Date;
  transactionFeePercent?: number;
  investments: Array<Api.InvestmentRow>;
  minimumInvestorPortfolioSize?: number;
  maximumSingleCompanyAllocation?: number;
  totalInvestmentExFee: number;
  isAutoAllocation: boolean;
  isFullyDeployed?: boolean;
  containsInvestmentsLimitedBySingleCompanyAllocation?: boolean;
};

// Add schema and type for auto allocation form
const autoAllocatingTranchesSchema = z.object({
  autoAllocatingTranches: z.array(
    z.object({
      _id: z.string(),
      minimumInvestorPortfolioSize: z.number(),
      maximumSingleCompanyAllocation: z.number(),
      initialTargetShares: z.number(),
      defaultTransactionFeePercent: z.number(),
      defaultTransactionFeeVatPercent: z.number(),
      order: z.number().optional(),
      isRemoved: z.boolean().optional(),
      finalDeal: z.boolean().optional(),
    }),
  ),
});

export type AutoAllocatingTranchesFormValues = {
  autoAllocatingTranches: Array<any>;
};

const CreateAllocation: FC = () => {
  const [activeStep, setActiveStep] = useState<Steps>(Steps.CompanyDetails);
  const [shareAllocationUploadId, setShareAllocationUploadId] = useState<
    string | null
  >(null);
  const [fundAutoAllocationResults, setFundAutoAllocationResults] = useState<
    Array<FundAllocation>
  >([]);
  const [newShareClassCreated, setNewShareClassCreated] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    null,
  );
  const [initialTargetShareSelected, setInitialTargetShareSelected] =
    useState<InitialTargetShareOption>(
      InitialTargetShareOption.InitialTargetShares,
    );

  // Function that resets all initialTargetShares to 0 when option changes
  const resetInitialTargetShares = () => {
    const currentTranches = autoAllocationFormMethods.getValues(
      'autoAllocatingTranches',
    );
    if (currentTranches && currentTranches.length > 0) {
      const updatedTranches = currentTranches.map((tranche) => ({
        ...tranche,
        initialTargetShares: 0,
      }));
      autoAllocationFormMethods.setValue(
        'autoAllocatingTranches',
        updatedTranches,
      );
    }
  };
  const [isAutoAllocation, setIsAutoAllocation] = useState(false);
  const [draftAllocationAcknowledged, setDraftAllocationAcknowledged] =
    useState(false);
  const [existingAllocationId, setExistingAllocationId] = useState<
    string | undefined
  >(undefined);
  const [existingDraftAllocationId, setExistingDraftAllocationId] = useState<
    string | undefined
  >(undefined);
  const [allocationFlags, setAllocationFlags] = useState<{
    containsExitedShareholdings: boolean;
    fundIdsInDraftAllocation: Array<string>;
    containsExportedTransactionFees: boolean;
    isShareConsiderationAllocation: boolean;
    exitedCompanyInfo: {
      tradingName: string;
      shareClass: string;
      exitId: string;
      exitAmendmentId?: string;
    } | null;
  }>({
    containsExitedShareholdings: false,
    fundIdsInDraftAllocation: [],
    containsExportedTransactionFees: false,
    isShareConsiderationAllocation: false,
    exitedCompanyInfo: null,
  });

  const { firmId } = useGetRole();
  const formWrapperRef = useRef<FormWrapperRef>(null);
  const {
    draftAllocationId: existingDraftAllocationIdParam,
    id: existingAllocationIdParam,
  } = useParams<{
    draftAllocationId?: string;
    id?: string;
  }>();
  const [preservedAutoAllocationInput, setPreservedAutoAllocationInput] =
    useState<Array<FundAllocation>>([]);

  const shouldFetchDraftAllocation =
    !!existingDraftAllocationIdParam &&
    existingDraftAllocationIdParam.trim() !== '' &&
    !draftAllocationAcknowledged;

  const {
    data: existingDraftAllocation,
    isLoading: isLoadingExistingDraftAllocation,
  } = api.eisAllocations.getDraftAllocation.useQuery(
    {
      id: shouldFetchDraftAllocation ? existingDraftAllocationIdParam : '',
    },
    {
      enabled: shouldFetchDraftAllocation,
    },
  );

  const shouldFetchAllocation =
    !!existingAllocationIdParam &&
    existingAllocationIdParam.trim() !== '' &&
    !draftAllocationAcknowledged;

  const {
    data: existingAllocation,
    isLoading: isLoadingExistingAllocation,
    isFetching,
    refetch: refetchAllocation,
  } = api.eisAllocations.getAllocation.useQuery(
    {
      // Always pass the ID param (or empty string as fallback), rely on enabled flag and manual guard
      id: existingAllocationIdParam || '',
    },
    {
      enabled: shouldFetchAllocation,
    },
  );

  // Only use the data if it's not currently fetching (ensures fresh data)
  const freshExistingAllocation = isFetching ? undefined : existingAllocation;
  const dealDetailsFormMethods = useForm<DealDetailsFormValues>({
    shouldFocusError: true,
    resolver: zodResolver(dealDetailsSchema),
    defaultValues: {
      dealSharePrice: undefined,
      allocationDate: new Date(),
      trancheIds: [],
    },
    criteriaMode: 'all',
    reValidateMode: 'onChange',
  });

  // Add form hook for auto allocation
  const autoAllocationFormMethods = useForm<AutoAllocatingTranchesFormValues>({
    defaultValues: {
      autoAllocatingTranches: [],
    },
    resolver: zodResolver(autoAllocatingTranchesSchema),
  });

  // If there is an existing draft or completed allocation, set the form values to that allocation
  // navigate to the review step
  useEffect(() => {
    const allocation = existingDraftAllocation || freshExistingAllocation;

    if (allocation && !isLoadingExistingDraftAllocation) {
      setActiveStep(Steps.Review);
      setAllocationFlags({
        containsExitedShareholdings:
          allocation?.containsExitedShareholdings ?? false,
        fundIdsInDraftAllocation:
          allocation?.fundIdsInDraftAllocation?.map((id) => id.toString()) ??
          [],
        containsExportedTransactionFees:
          allocation?.containsExportedTransactionFees ?? false,
        isShareConsiderationAllocation:
          allocation?.isShareConsiderationAllocation ?? false,
        exitedCompanyInfo: allocation?.exitedCompanyInfo ?? null,
      });

      setExistingAllocationId(existingAllocationIdParam);
      setExistingDraftAllocationId(existingDraftAllocationIdParam);
      setFundAutoAllocationResults(allocation?.allocationPreviews);
      setSelectedCompanyId(allocation?.allocationPreviews[0]?.companyId ?? '');
      setIsAutoAllocation(
        allocation?.allocationPreviews[0]?.isAutoAllocation ?? false,
      );
      setDraftAllocationAcknowledged(true);
      setPreservedAutoAllocationInput(allocation?.allocationPreviews);

      // Set trancheIds in deal details form from allocationPreviews trancheId values
      const trancheIds =
        allocation?.allocationPreviews?.map((preview) => preview.trancheId) ??
        [];
      dealDetailsFormMethods.setValue('trancheIds', trancheIds);
    }
  }, [
    existingDraftAllocation,
    freshExistingAllocation,
    dealDetailsFormMethods,
  ]);

  // Update allocation flags and data when existing allocation is refetched (e.g., after edit)
  useEffect(() => {
    if (
      freshExistingAllocation &&
      existingAllocationId &&
      draftAllocationAcknowledged
    ) {
      setAllocationFlags({
        containsExitedShareholdings:
          freshExistingAllocation?.containsExitedShareholdings ?? false,
        fundIdsInDraftAllocation:
          freshExistingAllocation?.fundIdsInDraftAllocation?.map((id) =>
            id.toString(),
          ) ?? [],
        containsExportedTransactionFees:
          freshExistingAllocation?.containsExportedTransactionFees ?? false,
        isShareConsiderationAllocation:
          freshExistingAllocation?.isShareConsiderationAllocation ?? false,
        exitedCompanyInfo: freshExistingAllocation?.exitedCompanyInfo ?? null,
      });

      // Also update all allocation-related state with fresh data
      setFundAutoAllocationResults(
        freshExistingAllocation?.allocationPreviews ?? [],
      );
    }
  }, [
    freshExistingAllocation,
    existingAllocationId,
    draftAllocationAcknowledged,
  ]);

  const breadcrumbs = [
    { label: 'Dashboard', link: '/' },
    { label: 'Create allocation', isActive: true },
  ];

  const handleClearAndRestart = () => {
    setNewShareClassCreated(false);
    setSelectedCompanyId(null);
    setActiveStep(Steps.CompanyDetails);
    // Reset the form values
    formWrapperRef.current?.reset();
    // Also reset the deal details form
    dealDetailsFormMethods.reset();
    // Reset auto allocation form
    autoAllocationFormMethods.reset();
    // Reset auto allocation state
    setIsAutoAllocation(false);
    // Reset auto allocation results
    setFundAutoAllocationResults([]);
    setExistingDraftAllocationId(undefined);
    setExistingAllocationId(undefined);
    setDraftAllocationAcknowledged(false);
    setPreservedAutoAllocationInput([]);
    setAllocationFlags({
      containsExitedShareholdings: false,
      fundIdsInDraftAllocation: [],
      containsExportedTransactionFees: false,
      isShareConsiderationAllocation: false,
      exitedCompanyInfo: null,
    });
    // Reset initial target share selection
    setInitialTargetShareSelected(InitialTargetShareOption.InitialTargetShares);
  };

  if (
    isLoadingExistingDraftAllocation ||
    isLoadingExistingAllocation ||
    isFetching
  ) {
    return <PageLoader />;
  }

  return (
    <PageContainer
      heading={`Allocations: ${
        existingAllocationId ? 'View' : 'Create new'
      } allocation`}
      breadcrumbs={breadcrumbs}
    >
      <PageContent withSpacing>
        <Spacing size="lg">
          {activeStep !== Steps.Success && !existingAllocationId && (
            <GridContainer>
              <Grid size={{ sm: 12, md: 12, lg: 10 }}>
                <Stepper
                  activeStep={existingAllocationId ? Steps.Success : activeStep}
                >
                  {Object.values(stepLabels).map((stepLabel) => (
                    <Step key={stepLabel}>
                      <StepLabel>{stepLabel}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Grid>
            </GridContainer>
          )}

          <FormWrapper
            ref={formWrapperRef}
            values={{
              isNewCompany: false,
              isNewShareClass: false,
              investeeId: '',
              tradingName: '',
              legalName: '',
              url: '',
              twitterUrl: '',
              linkedinUrl: '',
              companiesHouseNumber: '',
              description: '',
              companyLogo: null,
              previewCompanyLogo: null,
              shareClass: '',
              nominalShareValue: '',
              stage: '',
              sector: '',
              firmId: firmId ?? '',
              shareClassId: '',
            }}
          >
            {activeStep === Steps.CompanyDetails && (
              <CompanyDetails
                onSuccess={() => setActiveStep(Steps.DealDetails)}
                setSelectedCompanyId={(companyId) =>
                  setSelectedCompanyId(companyId)
                }
                setNewShareClassCreated={setNewShareClassCreated}
              />
            )}
          </FormWrapper>

          {activeStep === Steps.DealDetails && (
            <DealDetailsForm
              onReview={() => setActiveStep(Steps.Review)}
              onBack={() => setActiveStep(Steps.CompanyDetails)}
              companyId={selectedCompanyId ?? ''}
              isNewShareClassCreated={newShareClassCreated}
              handleClearAndRestart={handleClearAndRestart}
              setFundAutoAllocationResults={setFundAutoAllocationResults}
              formMethods={dealDetailsFormMethods}
              isAutoAllocation={isAutoAllocation}
              setIsAutoAllocation={setIsAutoAllocation}
              autoAllocationFormMethods={autoAllocationFormMethods}
              fundAutoAllocationResults={fundAutoAllocationResults}
              initialTargetShareSelected={initialTargetShareSelected}
              setInitialTargetShareSelected={setInitialTargetShareSelected}
              resetInitialTargetShares={resetInitialTargetShares}
              preservedAutoAllocationInput={preservedAutoAllocationInput}
            />
          )}

          {activeStep === Steps.Review && (
            <ReviewSection
              fundAllocations={fundAutoAllocationResults}
              companyId={selectedCompanyId ?? ''}
              setShareAllocationUploadId={setShareAllocationUploadId}
              onAllocationSuccess={() => setActiveStep(Steps.Success)}
              onReprocessAllocation={() => {
                setActiveStep(Steps.DealDetails);
                setExistingDraftAllocationId(undefined);
                setExistingAllocationId(undefined);
              }}
              isAutoAllocation={isAutoAllocation}
              setFundAutoAllocationResults={setFundAutoAllocationResults}
              existingAllocationId={existingAllocationId}
              draftAllocationId={existingDraftAllocationId}
              containsExitedShareholdings={
                allocationFlags.containsExitedShareholdings
              }
              fundIdsInDraftAllocation={
                allocationFlags.fundIdsInDraftAllocation
              }
              containsExportedTransactionFees={
                allocationFlags.containsExportedTransactionFees
              }
              isShareConsiderationAllocation={
                allocationFlags.isShareConsiderationAllocation
              }
              exitedCompanyInfo={allocationFlags.exitedCompanyInfo}
              setIsAutoAllocation={setIsAutoAllocation}
              refetchAllocation={() => {
                // Only refetch if we're viewing an existing completed allocation (not a draft)
                if (
                  existingAllocationId &&
                  existingAllocationIdParam &&
                  existingAllocationIdParam.trim() !== ''
                ) {
                  refetchAllocation();
                }
              }}
            />
          )}

          {activeStep === Steps.Success && (
            <SuccessPage
              selectedCompanyId={selectedCompanyId ?? ''}
              shareAllocationUploadId={shareAllocationUploadId ?? ''}
              allocationDate={fundAutoAllocationResults[0]?.allocationDate}
            />
          )}
        </Spacing>
      </PageContent>
    </PageContainer>
  );
};

export default CreateAllocation;
